/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { isClientItem, isCollectionAccessor, isScalarAccessor } from "abreact/data/DataValueAccessor";
export class AbDataValueUtils {
  static canModifyAccessor(accessor) {
    if (isScalarAccessor(accessor)) {
      return accessor.setClientValue !== undefined;
    }
    if (isCollectionAccessor(accessor)) {
      return accessor.addItem !== undefined || accessor.removeItem !== undefined;
    }
    return false;
  }
  static getClientItems(subject, accessor) {
    if (accessor.getItems) {
      return accessor.getItems(subject);
    } else if (accessor.getDecoratedItems) {
      return accessor.getDecoratedItems(subject).filter(isClientItem).map(item => item.value);
    } else {
      throw new Error(`Unable to determine client items for data value "${accessor.displayName}"`);
    }
  }
  static setClientItems(subject, accessor, items) {
    const addItemFunction = accessor.addItem;
    const removeItemFunction = accessor.removeItem;
    if (!accessor.cannotEdit || accessor.cannotEdit(subject)) {
      throw new Error(`Unable to set vector attribute ${accessor.displayName} as it is not editable${accessor.cannotEditReason ? `: ${accessor.cannotEditReason(subject)}` : ""}`);
    }
    if (!addItemFunction || !removeItemFunction) {
      throw new Error(`Unable to set vector attribute ${accessor.displayName} as addItem and removeItem methods are not both defined`);
    }
    const clientItems = AbDataValueUtils.getClientItems(subject, accessor);
    const itemsToAdd = new Set();
    // Initialize the records to remove to all the applicable records.
    // The walk below will remove records that are still in use in the local items.
    // The end result will be a set of records that are not in local items,
    // and hence should be removed.
    const itemsToRemove = new Set(clientItems);
    // Walk the local items to determine which ones to delete, and which ones need to be created.
    items.map(referencedItem => {
      const clientItem = AbDataValueUtils.findItem(subject, accessor, referencedItem, clientItems);
      if (clientItem !== undefined) {
        // This is the record for this item, so delete it from the set of records to be removed.
        itemsToRemove.delete(clientItem);
      } else {
        // This item does not have a record, so we need to add one for it.
        itemsToAdd.add(referencedItem);
      }
    });
    let newSubject = subject;

    // Remove all the items that are no longer included
    Array.from(itemsToRemove.values()).map(item => {
      newSubject = removeItemFunction(newSubject, item);
    });

    // Add any items that weren't previously included
    Array.from(itemsToAdd.values()).map(item => {
      newSubject = addItemFunction(newSubject, item);
    });
    return newSubject;
  }
  static findItem(subject, accessor, item, items) {
    const clientItems = items ?? AbDataValueUtils.getClientItems(subject, accessor);
    return clientItems.find(clientItem => {
      return AbDataValueUtils.areCollectionItemsEqual(accessor, item, clientItem);
    });
  }
  static areCollectionItemsEqual(accessor, item1, item2) {
    if (item1 == null || item2 == null) {
      return item1 === item2;
    }
    const itemEqualityFunction = accessor.itemEqualityFunction || ((item1, item2) => item1 === item2);
    return itemEqualityFunction(item1, item2);
  }
  static areItemsEqual(accessor, item1, item2) {
    if (item1 == null || item2 == null) {
      return item1 === item2;
    }
    const itemEqualityFunction = isCollectionAccessor(accessor) && accessor.itemEqualityFunction || ((item1, item2) => item1 === item2);
    return itemEqualityFunction(item1, item2);
  }

  /**
   * Returns true if a data value is modified according to its accessor.
   * <p>A client-only instance will always return false here.
   */
  static isDataValueModified(subject, accessor) {
    const isClientOnly = subject && (accessor.isClientOnly?.(subject) ?? false);
    const hasClientChange = subject && accessor.hasClientChange ? accessor.hasClientChange(subject) : false;
    const hasServerChange = subject && accessor.hasServerChange ? accessor.hasServerChange(subject) : false;
    return !isClientOnly && (hasClientChange || hasServerChange);
  }
  static canRevertDataValue(subject, accessor) {
    if (!subject) {
      return false;
    }
    if (accessor.canRevertClientChange) {
      return accessor.canRevertClientChange(subject);
    }
    if (accessor.revertClientChange !== undefined) {
      return true;
    }
    if (isScalarAccessor(accessor) && accessor.getServerValue) {
      const serverValue = accessor.getServerValue(subject);
      if (serverValue !== undefined) {
        const clientValue = accessor.getClientValue(subject);
        return !AbDataValueUtils.areItemsEqual(accessor, clientValue, serverValue ?? null);
      } else {
        return false;
      }
    }
    return false;
  }
  static revertDataValue(subject, accessor) {
    if (accessor.revertClientChange) {
      return accessor.revertClientChange(subject);
    }
    if (isScalarAccessor(accessor) && accessor.setClientValue && accessor.getServerValue) {
      return accessor.setClientValue(subject, accessor.getServerValue(subject) ?? null);
    }
    return undefined;
  }
}
//# sourceMappingURL=AbDataValueUtils.js.map
